import { BlueprintFlowData, FlowScenario } from '../../types/flowScenario';
import React from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import guessCallingCode from 'guess-calling-code';
import { LazyBlueprintWelcome } from './BlueprintWelcome/LazyBlueprintWelcome';
import { LazyAssessmentPage } from './AssessmentPage/LazyAssessmentPage';
import { LazyReportViewer } from './ReportViewer/LazyReportViewer';
import { LazyPostAssessmentPage } from './PostAssessmentPage/LazyPostAssessmentPage';
import { LazyPostConversation } from './PostConversation/LazyPostConversation';
import { LazyScheduleConversation } from './ScheduleConversation/LazyScheduleConversation';
import { LazyScheduleBlueprintSession } from './ScheduleBlueprintSession/LazyScheduleBlueprintSession';
import { LazyPaymentPage } from './PaymentPage/LazyPaymentPage';

export function useMainLineViewData(
  blueprintFlowData: BlueprintFlowData | undefined,
  flowScenario: FlowScenario
): {
  maineLineViewComponent: React.ReactNode;
} {
  const { user } = useAuthStore();
  const email = user?.email;
  const mainLineViewComponent = React.useMemo(() => {
    if (flowScenario !== 'blueprint') {
      // There should be some shared error view here
      return null;
    }

    if (!email) {
      // There should be some shared error view here
      return null;
    }

    if (!blueprintFlowData) {
      // There should be some shared error view here
      return null;
    }

    const currentState = blueprintFlowData.currentState;

    // Main Page
    if (currentState === 1) {
      const firstName = blueprintFlowData.firstName;

      return (
        <LazyBlueprintWelcome
          firstName={firstName}
          introFromGuideProps={{
            firstName: blueprintFlowData.guideDetails?.firstName || 'Carolyn',
            fullName: blueprintFlowData.guideDetails?.name || 'Carolyn Chittilappilly',
            gender: blueprintFlowData.guideDetails?.gender || 'Female',
            guideVideoUrl:
              blueprintFlowData.guideDetails?.videoLinks?.introduction ||
              (blueprintFlowData.guideDetails?.firstName === undefined
                ? 'https://www.youtube.com/watch?v=5J9-5OH9Umo'
                : undefined),
          }}
          faqProps={[
            {
              question: 'What is the Leela Life Blueprint?',
              answer:
                'Your Blueprint is a three-week guided exploration of who you naturally are, how those qualities find expression in your life today, and where there may be greater room for alignment.\n\nIt brings together your reflections, conversations with your Guide and perspectives from Indic wellbeing knowledge to create a deeply personal understanding of you and your life.',
            },
            {
              question: 'What happens over the next three weeks?',
              answer:
                'You will begin by reflecting on your life today. You will then have conversations with your Guide while, behind the scenes, your responses are explored through three perspectives from Indic knowledge.\n\nAs the journey unfolds, you will discover your innate qualities, where some may have become dormant, and what alignment could look like for you.',
            },
            {
              question: 'How much time should I set aside?',
              answer:
                'Your Blueprint unfolds over approximately three weeks. You will spend time on an initial reflection, conversations with your Guide, and reading and reflecting on what emerges.\n\nThere is no need to rush through it. The journey is intentionally spaced so you have time to notice what resonates and allow your understanding to deepen.',
            },
          ]}
        />
      );
    }

    // Payment Page - skip if offline payment was done
    if (currentState === 2) {
      if (blueprintFlowData.isOfflinePaymentDone) {
        return (
          <LazyScheduleBlueprintSession flowScenario={flowScenario} isOfflinePaymentDone={true} />
        );
      }
      return (
        <LazyPaymentPage
          actualPrice={blueprintFlowData.actualPrice}
          finalPrice={blueprintFlowData.currentPrice}
          discount={blueprintFlowData.discount}
        />
      );
    }

    // Schedule Conversation Page
    if (currentState === 3) {
      // Use new Blueprint Scheduling instead of Acuity
      // TODO: Add feature flag to toggle between Acuity and Blueprint scheduling
      const useBlueprintScheduling = true; // For now, always use new scheduling

      if (useBlueprintScheduling) {
        return (
          <LazyScheduleBlueprintSession
            flowScenario={flowScenario}
            isOfflinePaymentDone={blueprintFlowData.isOfflinePaymentDone}
          />
        );
      }

      return (
        <LazyScheduleConversation
          appointmentOwnerId={blueprintFlowData.appointmentOwnerId.toString()}
          appointmentType={blueprintFlowData.appointmentType.toString()}
          guideId={blueprintFlowData.guideId}
          email={email}
          firstName={blueprintFlowData.firstName}
          lastName={blueprintFlowData.lastName}
          phoneNumber={guessCallingCode()}
          appointmentName={blueprintFlowData.appointmentName}
        />
      );
    }

    // Assessment Page
    if (currentState === 4) {
      // Derive displayDateTime from bookingDetails if available, fallback to blueprintFlowData
      const bookingDetails = blueprintFlowData.bookingDetails;
      let displayDateTime = blueprintFlowData.displayDateTime;
      let schedulePageLink = blueprintFlowData.confirmationPageLink;

      // If bookingDetails exists, derive displayDateTime from scheduledTime in the booking's timezone
      if (bookingDetails?.scheduledTime?.start && bookingDetails?.scheduledTime?.timezone) {
        const startDate = new Date(bookingDetails.scheduledTime.start);

        // Get timezone abbreviation using locale-specific formatting
        const timeString = startDate.toLocaleString('en-US', {
          timeZone: bookingDetails.scheduledTime.timezone,
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
        });

        // Extract timezone abbreviation (everything after the last space)
        const tzMatch = timeString.match(/\s([A-Z]{2,5})$/);
        const timezoneName = tzMatch ? tzMatch[1] : 'IST';

        const formatter = new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZone: bookingDetails.scheduledTime.timezone,
        });

        displayDateTime = `${formatter.format(startDate)} ${timezoneName}`;
        // For new booking system, we don't have a reschedule link yet, so use a placeholder or empty
        schedulePageLink = schedulePageLink || '#';
      }

      // Only render if we have required fields
      if (displayDateTime && blueprintFlowData.formId) {
        return (
          <LazyAssessmentPage
            displayDateTime={displayDateTime}
            formId={blueprintFlowData.formId}
            schedulePageLink={schedulePageLink || '#'}
            email={email}
            firstName={blueprintFlowData.firstName}
            lastName={blueprintFlowData.lastName}
            flowScenario={flowScenario}
            useNewBookingSystem={!!bookingDetails}
            bookingId={bookingDetails?.bookingId}
            faqProps={[
              {
                question: 'Why are you asking me these questions?',
                answer:
                  'We want to understand both who you have naturally been and the life you are living today. Looking back helps surface early tendencies and qualities. Looking at your present life helps us explore which of these continue to flourish and which may have become quieter over time.\n\nBoth become important inputs to your Blueprint.',
              },
              {
                question: 'How long should I set aside?',
                answer:
                  'Most people take around **30-45 minutes**.\n\nThe questions are designed to invite reflection rather than quick answers. Choose a time when you can respond without rushing.',
              },
              {
                question: 'Will my Guide read what I share?',
                answer:
                  "Yes. Your Guide along with the experts will read your reflections before your conversation. Rather than treating individual answers as conclusions, your Guide will look for themes, patterns and areas worth exploring with you more deeply when you meet. This distinction is important. It reinforces that the questionnaire isn't an algorithmic assessment.",
              },
              {
                question: 'How does this become part of my Blueprint?',
                answer:
                  "Your reflections are one part of the investigation. They are considered alongside your conversations with your Guide and three different perspectives from Indic wellbeing knowledge. Your Guide and Leela Life's Indic wellbeing experts then look across these different perspectives for recurring patterns - helping build a picture of your innate qualities, your life today, and where greater alignment may be possible.",
              },
            ]}
          />
        );
      }
    }

    if (currentState === 5) {
      if (blueprintFlowData.conversationCompleted) {
        return (
          <LazyPostConversation
            guideVideoUrl={blueprintFlowData?.guideDetails?.videoLinks?.postConversation}
            dateTime={blueprintFlowData.dateTime}
          />
        );
      }

      if (
        (blueprintFlowData.dateTime &&
          blueprintFlowData.confirmationPageLink &&
          blueprintFlowData.displayDateTime) ||
        (blueprintFlowData.bookingDetails &&
          blueprintFlowData.bookingDetails.bookingId &&
          blueprintFlowData.bookingDetails.scheduledTime)
      ) {
        // Derive displayDateTime from bookingDetails if available, fallback to blueprintFlowData
        const bookingDetails = blueprintFlowData.bookingDetails;
        let displayDateTime = blueprintFlowData.displayDateTime;
        let schedulePageLink = blueprintFlowData.confirmationPageLink;
        const useNewBookingSystem = !!bookingDetails;

        // If bookingDetails exists, derive displayDateTime from scheduledTime in the booking's timezone
        if (bookingDetails?.scheduledTime?.start && bookingDetails?.scheduledTime?.timezone) {
          const startDate = new Date(bookingDetails.scheduledTime.start);

          // Get timezone abbreviation using locale-specific formatting
          const timeString = startDate.toLocaleString('en-US', {
            timeZone: bookingDetails.scheduledTime.timezone,
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
          });

          // Extract timezone abbreviation (everything after the last space)
          const tzMatch = timeString.match(/\s([A-Z]{2,5})$/);
          const timezoneName = tzMatch ? tzMatch[1] : 'IST';

          const formatter = new Intl.DateTimeFormat('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: bookingDetails.scheduledTime.timezone,
          });

          displayDateTime = `${formatter.format(startDate)} ${timezoneName}`;
        }

        return (
          <LazyPostAssessmentPage
            dateTime={
              blueprintFlowData.bookingDetails?.scheduledTime.start ??
              blueprintFlowData.dateTime ??
              ''
            }
            displayDateTime={displayDateTime ?? ''}
            schedulePageLink={schedulePageLink}
            useNewBookingSystem={useNewBookingSystem}
            guideName={blueprintFlowData.guideDetails?.name}
            guideVideoUrl={
              blueprintFlowData.guideDetails?.videoLinks?.postAssessment ||
              (blueprintFlowData.guideDetails?.firstName === undefined
                ? 'https://www.youtube.com/watch?v=qRBg4CtEk_E'
                : undefined)
            }
          />
        );
      }
    }

    if (currentState === 6) {
      return (
        <LazyReportViewer
          enableFeedback={blueprintFlowData.enableFeedback}
          feedbackDetails={blueprintFlowData.feedbackDetails}
          firstName={blueprintFlowData.firstName}
          lastName={blueprintFlowData.lastName}
          reportViewed={blueprintFlowData.reportViewed}
          phase2ReportViewed={blueprintFlowData.phase2ReportViewed}
          email={email}
          guideId={blueprintFlowData.guideId}
          phoneNumber={guessCallingCode()}
          reportUrl={blueprintFlowData.reportUrl}
          guideVideoUrl={blueprintFlowData.guideDetails?.videoLinks?.postReportUpload}
          reportV2={!!blueprintFlowData.reportV2}
          faqProps={[
            {
              question: 'What is the Wellbeing Blueprint?',
              answer:
                'The Wellbeing Blueprint is a personalized roadmap created for you through a deep, multi-stage process. It draws on your assessment responses, guided conversations, and the insights of three Indic knowledge experts. The Blueprint outlines your innate nature, diagnoses dormant innate qualities, and recommends a focus area from the 5x5 Framework.',
            },
            {
              question: 'How is the Blueprint created?',
              answer: `Your Blueprint is shaped by combining three layers:
* The assessment form, which captures your early and current life patterns
* The in-depth conversation with your Guide, which explores these patterns in more detail
* A collaborative review by three Indic wellbeing experts who identify your dominant and underexpressed innate qualities This integrative process allows us to create a deeply personalized and holistic path toward wellbeing.`,
            },
            {
              question: 'Can the Blueprint evolve as my life changes?',
              answer:
                'Yes. The Blueprint is a living document. As your life context shifts and you begin implementing the recommendations, new insights will emerge. You can return to your Guide for continued support and refinement so that the Blueprint remains relevant and impactful.',
            },
            {
              question: 'What if something in the Blueprint doesn’t resonate with me?',
              answer:
                'Your experience matters deeply. If something doesn’t resonate, you can discuss it with your Guide in your follow-up session. The Blueprint is not a rigid prescription — it is a starting point for your journey, meant to evolve with your lived experience.',
            },
            {
              question: 'What happens after I receive the Blueprint?',
              answer: `After receiving your blueprint, you are encouraged to
- Live with greater awareness — using the Blueprint to notice what energises or depletes
- Observe daily life — tuning into ease, discomfort, dissonance, and moments of natural flow
- Reawaken dormant innate qualities — the Blueprint highlights the qualities that have gone quiet. Your task is to create conditions for their return—these are the most promising levers for re-patterning life
- Make gradual, intentional shifts — not sweeping changes, but subtle, precise adjustments
- Experiment with discernment — trying what feels natural, adjusting based on response
- Engage selectively — with practices, tools, and conversations that feel right
Schedule a follow-up session with your Guide to understand your blueprint`,
            },
          ]}
        />
      );
    }

    return null;
  }, [blueprintFlowData, email, flowScenario]);

  return {
    maineLineViewComponent: mainLineViewComponent,
  };
}
